import { useCallback, useEffect, useRef, ReactNode } from 'react'
import { Box, Stack } from '@mui/material'
import { ifEnter } from '../../../utils'
import { LeafFixedMediaCard } from '@thriveglobal/thrive-web-leafkit'

export type CardProps = {
    id?: string
    imageSection?: ReactNode
    detailsSection?: ReactNode
    detailsHeight?: number
    rootMargin?: string
    root?: null | Element
    threshold?: number
    responsive?: boolean
    width?: CARD_WIDTH
    backgroundColor?: string
    showLoading?: boolean
    onClick?: () => void
    onEnter?: (entry: IntersectionObserverEntry) => void
}

const extractInteractiveActions = (
    cardDetailsComponent: ReactNode
): ReactNode | null => {
    if (!cardDetailsComponent || typeof cardDetailsComponent !== 'object')
        return null

    const props = (cardDetailsComponent as any).props
    return props?.bottomRightContent || null
}

export default function Card({
    id,
    imageSection,
    detailsSection,
    detailsHeight = 128,
    rootMargin = '0px 0px 0px 0px',
    root = null,
    threshold = 0.5,
    responsive,
    width = CARD_WIDTH.medium,
    backgroundColor,
    showLoading = false,
    onClick,
    onEnter
}: CardProps) {
    const ref = useRef<HTMLDivElement>(null)

    const handleOnClick = useCallback(() => {
        if (!showLoading && onClick) {
            onClick()
        }
    }, [onClick, showLoading])

    useEffect(() => {
        if (!onEnter || !ref.current) return

        const el = ref.current

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) onEnter(entry)
            },
            { rootMargin, threshold, root }
        )

        observer.observe(el)

        return () => observer.unobserve(el)
    }, [onEnter, root, rootMargin, threshold])

    const interactiveActions = extractInteractiveActions(detailsSection)

    let accessibleDetailsSection = detailsSection
    if (
        interactiveActions &&
        detailsSection &&
        typeof detailsSection === 'object'
    ) {
        accessibleDetailsSection = {
            ...detailsSection,
            props: {
                ...(detailsSection as any).props,
                bottomRightContent: null
            }
        }
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: responsive ? '100%' : width,
                height: '100%'
            }}
        >
            <Box
                onClick={onClick ? handleOnClick : undefined}
                onKeyUp={onClick ? ifEnter(handleOnClick) : undefined}
                tabIndex={onClick ? 0 : undefined}
                role={onClick ? 'button' : undefined}
                aria-label={onClick && id ? `View ${id} details` : undefined}
                sx={{
                    cursor: onClick ? 'pointer' : 'default',
                    '&:focus': {
                        outline: onClick ? '2px solid #2196f3' : 'none'
                    },
                    height: '100%',
                    width: '100%'
                }}
            >
                <LeafFixedMediaCard
                    media={<>{imageSection}</>}
                    alignMedia="top"
                    sx={{
                        backgroundColor: backgroundColor || undefined,
                        width: '100%',
                        height: '100%'
                    }}
                    ref={ref}
                    data-testid={`learn-card-${id}`}
                    actionAreaProps={undefined}
                >
                    <Stack
                        justifyContent={'space-between'}
                        height={'100%'}
                        width={'100%'}
                        gap={2}
                    >
                        <Stack width={'100%'} height={`${detailsHeight}px`}>
                            {accessibleDetailsSection}
                        </Stack>
                    </Stack>
                </LeafFixedMediaCard>
            </Box>

            {/* Interactive action buttons positioned outside the clickable area */}
            {interactiveActions && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '16px',
                        right: '16px',
                        zIndex: 2
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {interactiveActions}
                </Box>
            )}
        </Box>
    )
}

export enum CARD_WIDTH {
    small = 153,
    medium = 367,
    large = 567
}
